import { Box, Container, Paper, useTheme, Grid, Typography, Stack, Divider, useMediaQuery } from "@mui/material";
import OpeningHoursItem from "./OpeningHoursItem";
import AboutSection from "./AboutSection";
import GallerySection from "./GallerySection";
import BlogPostsSection from "./BlogPostsSection";
import { Routes } from "../../routes/RouteConstants";
import CustomButton from "../../components/CustomButton";
import { useNavigate } from "react-router-dom";
import { useTenant } from "../../contexts/TenantContext";
import useRevealOnScroll from "../../hooks/useRevealOnScroll";
import useBlogPosts from "../../hooks/useBlogPosts";
import { sectionSx, heroSectionSx, frameSx } from "./sectionStyles";
import { gradientTextSx, sectionTitleSx } from "../../utils/textStyles";
import { isDarkTheme } from "../../utils/themeTraits";
import { SECTION_SCROLL_MARGIN } from "../../layouts/layoutMetrics";

export default function HomeLayout() {
  const { tenant, displayConfig, homePage } = useTenant();
  const tenantId = tenant?.tenantId;
  const openingHours = homePage?.openingHours
  // Below `md` the welcome card drops under the hero instead of overlapping its right side, so a
  // tenant whose hero is composed around that overlap can show another part of the same picture
  // there: its own ratio, and which part of the image fills it. Empty keeps the whole 3:2 photo.
  const narrowHero = homePage?.heroNarrowView
  const blogPosts = useBlogPosts();

  // Below `md` the dark themes' welcome card is a framed card under the hero, so it keeps a margin
  // on both sides; full width, the row's `overflow: hidden` cut its side borders off at the screen's
  // edges. Paper's is a panel flush with the photo (heroSectionSx) and stays full width.
  const gutter = isDarkTheme(displayConfig) ? { xs: 2, sm: 3 } : { xs: 0, sm: 0 };
  const welcomeCardPl = { ...gutter, md: 0 };
  const welcomeCardPr = { ...gutter, md: 1 };

  const theme = useTheme();
  const downXl = useMediaQuery(theme.breakpoints.down('xl'));
  const navigate = useNavigate();

  // Nothing on this page starts at the same moment as anything else. The order on load is
  // the hero image, then the welcome card beside it, then the header above them both
  // (`HEADER_DELAY_MS` in src/layouts/Header.js, after these two) — and everything below the
  // fold waits to be scrolled to, "Rólunk" first.
  //
  // Within the pairs the two halves share a duration and differ only in delay, so they read
  // as one movement rather than two animations that happen to overlap.
  const hero = useRevealOnScroll({ direction: "left", distance: 140, duration: 1800 });
  const welcomeCard = useRevealOnScroll({ direction: "right", distance: 140, duration: 2600, delay: 400 });
  const openingHoursCard = useRevealOnScroll({ direction: "left", distance: 50, duration: 1500, threshold: 0 });
  const map = useRevealOnScroll({ direction: "right", distance: 50, duration: 1500, threshold: 0 });

  return (
    <>
      <Container maxWidth="xl" disableGutters={downXl}>
        <Grid container overflow="hidden">
          <Grid item xs={12} md={9} lg={10} display="flex" justifyContent="center">
            <Box
              ref={hero.ref}
              component="img"
              src={`/${tenantId}/hero_image.jpg`}
              alt=""
              // The largest thing on the page and the one the reveal waits for, so it must
              // not queue behind the logo and the chunk requests.
              fetchPriority="high"
              sx={{
                width: "100%",
                // Reserve the hero's height before the jpeg decodes. With no ratio the image
                // has no intrinsic size on first paint, so this row collapses to the welcome
                // card beside it — nothing at all on xs, where the two stack — leaving the
                // page some 600px short. Everything below then starts inside the viewport,
                // every scroll reveal fires at mount, and the animations are over by the time
                // the loaded image pushes them past the fold.
                // Every tenant hero is 3:2 today; one that is not gets cropped by objectFit,
                // exactly as maxHeight already crops it. Below `md` a tenant's heroNarrowView
                // crops it on purpose, to another ratio and from another point.
                aspectRatio: { xs: narrowHero?.aspectRatio || "3 / 2", md: "3 / 2" },
                maxHeight: { lg: "90vh", xl: "70vh" },
                objectFit: 'cover',
                objectPosition: { xs: narrowHero?.objectPosition || "50% 50%", md: "50% 50%" },
                ...hero.revealSx
              }}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={2} py={2} pl={welcomeCardPl} pr={welcomeCardPr} bgcolor={{ xs: "#000000ff", md: "transparent" }} display="flex" alignItems="center" justifyContent={{ xs: "center", md: "end" }}>
            <Paper ref={welcomeCard.ref} elevation={0} sx={{
              ...heroSectionSx(displayConfig),
              pt: { xs: 4, md: 5 }, pb: { xs: 4, md: 5 }, px: 0,
              minWidth: { xs: "100%", md: "45vw", lg: "35vw", xl: "30vw", xxl: "25vw", xxxl: "16vw" },
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              ...welcomeCard.revealSx,
             }}>
              {homePage?.isBusinessNameLong ? (
                <Typography
                  variant="h2"
                  fontSize={24}
                  align="center"
                  color="grey"
                  fontWeight="bold"
                  mb={3}
                >
                  Üdvözöllek a 
                  <Typography variant="h1" fontSize={32} sx={{ whiteSpace: "nowrap", ...gradientTextSx(displayConfig, homePage?.businessNameColor) }}>
                    {homePage?.businessName}
                  </Typography>
                  oldalán!
                </Typography>
              ) : (
                  <Typography
                    variant="h1"
                    fontSize={{ xs: 22, sm: 36, md: 24, lg: 25, xl: 28 }}
                    align="center"
                    fontWeight="bold"
                    sx={{ px: 2 }}
                    mb={{ xs: 1, sm: 2, md: 1 }}
                  >
                    Üdvözöllek a{" "}
                    <Box component="span" sx={gradientTextSx(displayConfig, homePage?.businessNameColor)}>
                      {homePage?.businessName}
                    </Box>{" "}
                    oldalán!
                  </Typography>
              )
}
               <CustomButton text="Foglalás" onClick={() => navigate(Routes.BOOKING)} />
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <AboutSection />

      {/* Same order as the header menu: "Rólunk", the tenant's own posts, "Galéria". */}
      <BlogPostsSection posts={blogPosts} />

      <GallerySection />

      {/* The map travels in from the right, which overflows this container while it moves.
          `clip` rather than `hidden`: it keeps the box out of the scroll machinery, so no
          horizontal scrollbar flashes for the length of the animation. */}
      <Container maxWidth="xl" sx={{ overflowX: "clip" }}>
        {/* Side by side the two are one band: the opening hours set the height and the map
            stretches to it, so neither ends short of the other. Stacked, the map keeps a height
            of its own. */}
        <Stack direction={{ xs: "column", lg: "row" }} alignItems={{ xs: "center", lg: "stretch" }} spacing={2} py={{ xs: 3, lg: 4, xl: 5 }}>
          <Paper ref={openingHoursCard.ref} id="openingHours" elevation={0}
            sx={{
              ...sectionSx(displayConfig),
              scrollMarginTop: SECTION_SCROLL_MARGIN,
              minWidth: { xs: "100%", sm: "70%", md: "800px", lg: "800px", xl: "800px" },
              py: 3, display: "flex", flexDirection: "column", alignItems: "center",
              ...openingHoursCard.revealSx }}>
            <Typography variant="h4" fontWeight="bold" mb={3} sx={sectionTitleSx(displayConfig)}>
              Nyitvatartás
            </Typography>

            <Stack width={{ xs: "85%", sm: "70%", md: "35%" }} divider={<Divider orientation="horizontal" flexItem/>}>
              <OpeningHoursItem day={"Hétfő"} hours={openingHours?.monday} />
              <OpeningHoursItem day={"Kedd"} hours={openingHours?.tuesday} />
              <OpeningHoursItem day={"Szerda"} hours={openingHours?.wednesday} />
              <OpeningHoursItem day={"Csütörtök"} hours={openingHours?.thursday} />
              <OpeningHoursItem day={"Péntek"} hours={openingHours?.friday} />
              <OpeningHoursItem day={"Szombat"} hours={openingHours?.saturday} />
              <OpeningHoursItem day={"Vasárnap"} hours={openingHours?.sunday} />
            </Stack >
          </Paper>

          <Box component="iframe"
            ref={map.ref}
            title="Google Maps"
            src={homePage?.googleMapsUrl}
            width="100%"
            minHeight={{ xs: "50vh", lg: 0 }}
            maxHeight={{ xs: "600px", lg: "none" }}
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
            sx={{
              ...frameSx(displayConfig),
              ...map.revealSx }}
          />
        </Stack>
      </Container>
    </>
  )
}
